"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
exports.__esModule = true;
var eventIds = new Map([
    ["chatMessageReceived", 200],
    ["typingIndicatorReceived", 245],
    ["readReceiptReceived", 246],
    ["chatMessageEdited", 247],
    ["chatMessageDeleted", 248],
    ["chatThreadCreated", 257],
    ["chatThreadPropertiesUpdated", 258],
    ["chatThreadDeleted", 259],
    ["participantsAdded", 260],
    ["participantsRemoved", 261]
]);
var publicTeamsUserPrefix = '8:orgid:';
var dodTeamsUserPrefix = '8:dod:';
var gcchTeamsUserPrefix = '8:gcch:';
var teamsVisitorUserPrefix = '8:teamsvisitor:';
var phoneNumberPrefix = '4:';
var acsUserPrefix = '8:acs:';
var spoolUserPrefix = '8:spool:';
exports.toMessageHandler = function (event, listener) {
    var eventId = eventIds.get(event);
    return {
        handleMessage: function (message) {
            var genericPayload = null;
            if (message === null || message === void 0 ? void 0 : message.rawBody) {
                genericPayload = JSON.parse(message.rawBody);
            }
            if (genericPayload === null || genericPayload.eventId !== eventId) {
                return undefined;
            }
            var eventPayload = toEventPayload(event, genericPayload);
            if (eventPayload === null) {
                return undefined;
            }
            listener(eventPayload);
            return { isHandled: true, resultCode: 200 };
        }
    };
};
var toEventPayload = function (event, genericPayload) {
    if (event === "chatMessageReceived") {
        var payload = genericPayload;
        var eventPayload = {
            threadId: payload.groupId,
            sender: constructIdentifierKindFromMri(payload.senderId),
            senderDisplayName: payload.senderDisplayName,
            recipient: constructIdentifierKindFromMri(payload.recipientMri),
            id: payload.messageId,
            createdOn: new Date(payload.originalArrivalTime),
            version: payload.version,
            type: payload.messageType,
            message: payload.messageBody,
            metadata: parseMetadata(payload.acsChatMessageMetadata)
        };
        return eventPayload;
    }
    if (event === "chatMessageEdited") {
        var payload = genericPayload;
        var eventPayload = {
            threadId: payload.groupId,
            sender: constructIdentifierKindFromMri(payload.senderId),
            senderDisplayName: payload.senderDisplayName,
            recipient: constructIdentifierKindFromMri(payload.recipientMri),
            id: payload.messageId,
            createdOn: new Date(payload.originalArrivalTime),
            version: payload.version,
            message: payload.messageBody,
            editedOn: new Date(payload.edittime),
            type: payload.messageType,
            metadata: parseMetadata(payload.acsChatMessageMetadata)
        };
        return eventPayload;
    }
    if (event === "chatMessageDeleted") {
        var payload = genericPayload;
        var eventPayload = {
            threadId: payload.groupId,
            sender: constructIdentifierKindFromMri(payload.senderId),
            senderDisplayName: payload.senderDisplayName,
            recipient: constructIdentifierKindFromMri(payload.recipientMri),
            id: payload.messageId,
            createdOn: new Date(payload.originalArrivalTime),
            version: payload.version,
            deletedOn: new Date(payload.deletetime),
            type: payload.messageType
        };
        return eventPayload;
    }
    if (event === "typingIndicatorReceived") {
        var payload = genericPayload;
        var eventPayload = {
            threadId: payload.groupId,
            sender: constructIdentifierKindFromMri(payload.senderId),
            senderDisplayName: payload.senderDisplayName,
            recipient: constructIdentifierKindFromMri(payload.recipientMri),
            version: payload.version,
            receivedOn: new Date(payload.originalArrivalTime)
        };
        return eventPayload;
    }
    if (event === "readReceiptReceived") {
        var payload = genericPayload;
        var readReceiptMessageBody = JSON.parse(payload.messageBody);
        var consumptionHorizon = readReceiptMessageBody.consumptionhorizon.split(";");
        var eventPayload = {
            threadId: payload.groupId,
            sender: constructIdentifierKindFromMri(payload.senderId),
            senderDisplayName: "",
            recipient: constructIdentifierKindFromMri(payload.recipientMri),
            chatMessageId: payload.messageId,
            readOn: new Date(+consumptionHorizon[1])
        };
        return eventPayload;
    }
    if (event === "chatThreadCreated") {
        var payload = genericPayload;
        var createdByPayload = JSON.parse(unescape(payload.createdBy));
        var membersPayload = JSON.parse(unescape(payload.members));
        var propertiesPayload = JSON.parse(unescape(payload.properties));
        var createdBy = {
            id: constructIdentifierKindFromMri(createdByPayload.participantId),
            displayName: createdByPayload.displayName
        };
        var chatParticipants = membersPayload.map(function (m) {
            return {
                id: constructIdentifierKindFromMri(m.participantId),
                displayName: m.displayName
            };
        });
        var eventPayload = {
            threadId: payload.threadId,
            createdOn: new Date(payload.createTime),
            createdBy: createdBy,
            version: payload.version,
            participants: chatParticipants,
            properties: propertiesPayload
        };
        return eventPayload;
    }
    if (event === "chatThreadPropertiesUpdated") {
        var payload = genericPayload;
        var updatedByPayload = JSON.parse(unescape(payload.editedBy));
        var propertiesPayload = JSON.parse(unescape(payload.properties));
        var updatedBy = {
            id: constructIdentifierKindFromMri(updatedByPayload.participantId),
            displayName: updatedByPayload.displayName
        };
        var eventPayload = {
            threadId: payload.threadId,
            updatedOn: new Date(payload.editTime),
            updatedBy: updatedBy,
            version: payload.version,
            properties: propertiesPayload
        };
        return eventPayload;
    }
    if (event === "chatThreadDeleted") {
        var payload = genericPayload;
        var deletedByPayload = JSON.parse(unescape(payload.deletedBy));
        var deletedBy = {
            id: constructIdentifierKindFromMri(deletedByPayload.participantId),
            displayName: deletedByPayload.displayName
        };
        var eventPayload = {
            threadId: payload.threadId,
            deletedOn: new Date(payload.deleteTime),
            deletedBy: deletedBy,
            version: payload.version
        };
        return eventPayload;
    }
    if (event === "participantsAdded") {
        var payload = genericPayload;
        var addedByPayload = JSON.parse(unescape(payload.addedBy));
        var participantsAddedPayload = JSON.parse(unescape(payload.participantsAdded));
        var addedBy = {
            id: constructIdentifierKindFromMri(addedByPayload.participantId),
            displayName: addedByPayload.displayName
        };
        var chatParticipants = participantsAddedPayload.map(function (m) {
            return {
                id: constructIdentifierKindFromMri(m.participantId),
                displayName: m.displayName,
                shareHistoryTime: new Date(m.shareHistoryTime)
            };
        });
        var eventPayload = {
            threadId: payload.threadId,
            addedOn: new Date(payload.time),
            addedBy: addedBy,
            version: payload.version,
            participantsAdded: chatParticipants
        };
        return eventPayload;
    }
    if (event === "participantsRemoved") {
        var payload = genericPayload;
        var removedByPayload = JSON.parse(unescape(payload.removedBy));
        var participantsRemovedPayload = JSON.parse(unescape(payload.participantsRemoved));
        var removedBy = {
            id: constructIdentifierKindFromMri(removedByPayload.participantId),
            displayName: removedByPayload.displayName
        };
        var chatParticipants = participantsRemovedPayload.map(function (m) {
            return {
                id: constructIdentifierKindFromMri(m.participantId),
                displayName: m.displayName,
                shareHistoryTime: new Date(m.shareHistoryTime)
            };
        });
        var eventPayload = {
            threadId: payload.threadId,
            removedOn: new Date(payload.time),
            removedBy: removedBy,
            version: payload.version,
            participantsRemoved: chatParticipants
        };
        return eventPayload;
    }
    return null;
};
exports.toLogProvider = function (logger) {
    return {
        log: function () {
            var message = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                message[_i] = arguments[_i];
            }
            return logger.info(message);
        },
        warn: function () {
            var message = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                message[_i] = arguments[_i];
            }
            return logger.warning(message);
        },
        error: function () {
            var message = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                message[_i] = arguments[_i];
            }
            return logger.error(message);
        },
        debug: function () {
            var message = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                message[_i] = arguments[_i];
            }
            return logger.verbose(message);
        },
        info: function () {
            var message = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                message[_i] = arguments[_i];
            }
            return logger.verbose(message);
        }
    };
};
exports.toTelemetrySender = function (logger) {
    return {
        logEvent: function (clientEvent) { return logger.info(clientEvent); }
    };
};
var constructIdentifierKindFromMri = function (mri) {
    if (mri.startsWith(publicTeamsUserPrefix)) {
        return { kind: "microsoftTeamsUser", rawId: mri, microsoftTeamsUserId: mri.substring(publicTeamsUserPrefix.length), isAnonymous: false, cloud: 'public' };
    }
    else if (mri.startsWith(dodTeamsUserPrefix)) {
        return { kind: "microsoftTeamsUser", rawId: mri, microsoftTeamsUserId: mri.substring(dodTeamsUserPrefix.length), isAnonymous: false, cloud: 'dod' };
    }
    else if (mri.startsWith(gcchTeamsUserPrefix)) {
        return { kind: "microsoftTeamsUser", rawId: mri, microsoftTeamsUserId: mri.substring(gcchTeamsUserPrefix.length), isAnonymous: false, cloud: 'gcch' };
    }
    else if (mri.startsWith(teamsVisitorUserPrefix)) {
        return { kind: "microsoftTeamsUser", rawId: mri, microsoftTeamsUserId: mri.substring(teamsVisitorUserPrefix.length), isAnonymous: true };
    }
    else if (mri.startsWith(phoneNumberPrefix)) {
        return { kind: "phoneNumber", rawId: mri, phoneNumber: mri.substring(phoneNumberPrefix.length) };
    }
    else if (mri.startsWith(acsUserPrefix) || mri.startsWith(spoolUserPrefix)) {
        return { kind: "communicationUser", communicationUserId: mri };
    }
    else {
        return { kind: "unknown", id: mri };
    }
};
var parseMetadata = function (metadata) {
    if (metadata === undefined || metadata === null || metadata === "" || metadata === "null") {
        return {};
    }
    return JSON.parse(metadata);
};
